import * as PIXI from 'pixi.js';
import TextLine from './TextLine';

/**
 * TextBlock opens up the ability to animate each individual word in a block of text.
 *
 * By default, the appearance of the TextBlock will be the same as regular PIXI.Text given the same style and text.
 * TextBlock builds on TextLine, extending its capabilities to multiline text.
 */
export default class TextBlock extends PIXI.Container {
  text = '';
  lineStrings = [];
  lines = [];
  lineHeight = 0;
  blockHeight = 0;
  blockWidth = 0;
  numWords = 0;

  constructor(app) {
    super();

    this.app = app;
  }

  /**
   * Update text and style for the text block, position words
   */
  updateTextAndStyle(text, model, options = {}) {
    if (this.lines.length) {
      this.removeChild(...this.lines);
    }

    this.text = text;
    this.lineStrings = text.trim().split('\n') || [];
    this.lineHeight = 0;
    this.blockHeight = 0;
    this.blockWidth = 0;
    this.numWords = 0;

    // Create separate TextLine objects for each line of the input text
    this.lines = this.lineStrings.map((lineString, index) => {
      const line = new TextLine(this.app);
      line.updateTextAndStyle(lineString, model, options);
      line.y = line.lineHeight * index;

      this.lineHeight = line.lineHeight;
      this.blockWidth = Math.max(this.blockWidth, line.width);
      this.numWords += line.words.length;

      return line;
    });

    this.blockHeight = this.lineHeight * this.lines.length;
    if (this.lines.length) {
      this.addChild(...this.lines);
    }

    // alignment must happen at the end, since we don't know the block width until all text lines have been initialized
    switch (model.style.align) {
      case 'right':
        this.alignRight();
        break;
      case 'center':
        this.alignCenter();
        break;
      default:
        this.alignLeft();
    }
  }

  /**
   * Apply the callback to each word in the text block
   */
  forEachWord(callback) {
    let index = -1;
    this.lines.forEach((line, lineIndex) =>
      line.forEachWord((text, info) => {
        const { word, xPosition } = info;

        index += 1;

        return callback(text, {
          index,
          line,
          lineIndex,
          lineString: this.lineStrings[lineIndex],
          word,
          wordIndex: info.index,
          xPosition,
          yPosition: this.lineHeight * lineIndex,
        });
      })
    );
  }

  alignCenter() {
    this.lines.forEach((line) => {
      line.x = (this.blockWidth - line.width) / 2;
    });
  }

  alignLeft() {
    this.lines.forEach((line) => {
      line.x = 0;
    });
  }

  alignRight() {
    this.lines.forEach((line) => {
      line.x = this.blockWidth - line.width;
    });
  }
}
